import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Image as ImageIcon, Upload, Loader2, BookOpen, Calendar, Type, AlignLeft } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { Modal } from '../common';
import toast from 'react-hot-toast';

const todayStr = () => new Date().toISOString().slice(0, 10);

const CreateBitacoraModal = ({ isOpen, onClose, onSuccess, entrada = null }) => {
  const isEditing = Boolean(entrada);

  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fecha, setFecha] = useState(todayStr());
  // Cada imagen nueva: { file, preview }
  const [images, setImages] = useState([]);
  // Imágenes ya guardadas (solo en modo edición): { id, image_url }
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const imageInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    if (entrada) {
      setNombre(entrada.nombre || '');
      setDescripcion(entrada.descripcion || '');
      setFecha(entrada.fecha || todayStr());
      setExistingImages(entrada.imagenes_detalle || []);
    }
  }, [isOpen, entrada]);

  const resetForm = () => {
    setNombre('');
    setDescripcion('');
    setFecha(todayStr());
    setImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.preview));
      return [];
    });
    setExistingImages([]);
    setRemovedImageIds([]);
    setError(null);
  };

  const removeExistingImage = (id) => {
    setRemovedImageIds((prev) => [...prev, id]);
    setExistingImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleImageSelect = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const validos = files.filter((f) => f.type.startsWith('image/'));
    const invalidos = files.length - validos.length;

    if (validos.length > 0) {
      setImages((prev) => [
        ...prev,
        ...validos.map((file) => ({ file, preview: URL.createObjectURL(file) })),
      ]);
      setError(invalidos > 0 ? 'Se omitieron archivos no soportados.' : null);
    } else if (invalidos > 0) {
      setError('Formato no soportado. Usa imágenes (PNG, JPG, GIF, WEBP).');
    }
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setImages((prev) => {
      const img = prev[index];
      if (img) URL.revokeObjectURL(img.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleImageSelect(e.dataTransfer.files);
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    if (!fecha) {
      setError('La fecha es obligatoria');
      return;
    }

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('nombre', nombre.trim());
    formData.append('descripcion', descripcion.trim());
    formData.append('fecha', fecha);
    images.forEach((img) => formData.append('image', img.file));
    if (isEditing) {
      removedImageIds.forEach((id) => formData.append('eliminar_imagenes', id));
    }

    try {
      if (isEditing) {
        await axiosInstance.put(`/api/bitacora/${entrada.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Entrada de bitácora actualizada');
      } else {
        await axiosInstance.post('/api/bitacora', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Entrada de bitácora agregada');
      }
      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error guardando entrada de bitácora:', err);
      setError(err.response?.data?.error || 'Error al guardar la entrada');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header onClose={handleClose}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-sura-blue to-sura-blue-light rounded-xl text-white shadow-lg shadow-sura-blue/20">
            <BookOpen size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{isEditing ? 'Editar Entrada de Bitácora' : 'Nueva Entrada de Bitácora'}</h3>
            <p className="text-xs text-gray-500 font-normal">Servicios Generales</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit} id="create-bitacora-form" className="space-y-4">
          <div>
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">
              <Type size={13} /> Nombre
            </label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Mantenimiento de jardines - Sede Principal"
              className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition-all"
              maxLength={200}
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">
              <Calendar size={13} /> Fecha en que se realizó
            </label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition-all"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">
              <AlignLeft size={13} /> Descripción
            </label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Detalles de lo realizado..."
              rows={4}
              className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition-all resize-none"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">
              <ImageIcon size={13} /> Imágenes
              {(images.length + existingImages.length) > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-sura-blue/10 text-sura-blue text-[10px] font-bold normal-case tracking-normal">
                  {images.length + existingImages.length}
                </span>
              )}
            </label>

            {existingImages.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5 mb-2.5">
                {existingImages.map((img, index) => (
                  <div
                    key={img.id}
                    className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-900/95 group"
                  >
                    <img src={img.image_url} alt={`Imagen actual ${index + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeExistingImage(img.id)}
                      className="absolute top-1 right-1 p-1 bg-white/90 backdrop-blur-sm rounded-full shadow-md text-gray-600 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100"
                      title="Quitar"
                    >
                      <X size={13} />
                    </button>
                    {index === 0 && (
                      <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[9px] font-semibold">
                        Portada
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-xl p-4 transition-all duration-200 ${
                dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-gray-50/50'
              }`}
            >
              {images.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5 mb-3">
                  {images.map((img, index) => (
                    <div
                      key={img.preview}
                      className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-900/95 group"
                    >
                      <img src={img.preview} alt={`Imagen ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 p-1 bg-white/90 backdrop-blur-sm rounded-full shadow-md text-gray-600 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100"
                        title="Quitar"
                      >
                        <X size={13} />
                      </button>
                      {index === 0 && existingImages.length === 0 && (
                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[9px] font-semibold">
                          Portada
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col items-center gap-2 py-2">
                <div className={`p-2.5 rounded-full transition-colors ${dragActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <Upload size={18} className={dragActive ? 'text-blue-600' : 'text-gray-400'} />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">
                    Arrastra imágenes aquí o
                  </p>
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 mt-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                  >
                    <ImageIcon size={16} className="text-blue-600" />
                    {images.length > 0 ? 'Agregar más' : 'Seleccionar imágenes'}
                  </button>
                  <p className="text-xs text-gray-400 mt-2">Puedes subir varias — PNG, JPG, GIF o WEBP</p>
                </div>
              </div>

              <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
                multiple
                onChange={(e) => handleImageSelect(e.target.files)}
                className="hidden"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm animate-fadeIn">
              <X size={16} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </form>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-end w-full gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="create-bitacora-form"
            disabled={loading}
            className="px-6 py-2.5 font-semibold rounded-xl transition-all flex items-center gap-2 shadow-sm bg-gradient-to-r from-sura-blue to-sura-blue-light text-white hover:from-sura-blue-dark hover:to-sura-blue shadow-sura-blue/20 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? 'Guardando...' : isEditing ? 'Guardar Cambios' : 'Guardar Entrada'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateBitacoraModal;
